import { CLIENT_HEADERS, CLIENT_SCHEMA_VERSION, } from './contract.js';
export class UploadError extends Error {
    status;
    constructor(status, message) {
        super(message);
        this.name = 'UploadError';
        this.status = status;
    }
}
/** Translates a PowerSync CRUD entry into an upload operation of the API contract. */
export function toUploadOperation(entry) {
    const op = entry.op;
    const operation = { op, table: entry.table, id: entry.id };
    if (op !== 'DELETE') {
        operation.data = { ...(entry.opData ?? {}) };
    }
    return operation;
}
export class FlowSyncConnector {
    options;
    fetchImpl;
    credentials = null;
    outdated = false;
    status = 'idle';
    constructor(options) {
        this.options = options;
        this.fetchImpl = options.fetch ?? fetch;
    }
    get currentStatus() {
        return this.status;
    }
    /** True after a `426 client_outdated`: the queue is kept and uploads are paused. */
    get isOutdated() {
        return this.outdated;
    }
    /** Resumes uploads (after the app has been updated). */
    resetOutdated() {
        this.outdated = false;
        this.setStatus('idle');
    }
    /** Discards the cached token (e.g. after sign-out or a `401`). */
    invalidateCredentials() {
        this.credentials = null;
    }
    async fetchCredentials() {
        const margin = (this.options.tokenRefreshMarginSeconds ?? 60) * 1000;
        const cached = this.credentials;
        if (cached?.expiresAt && cached.expiresAt.getTime() - Date.now() > margin) {
            return cached;
        }
        const response = await this.fetchImpl(`${this.options.apiUrl}/v1/sync/token`, {
            method: 'GET',
            headers: this.headers(),
            credentials: 'include',
        });
        if (!response.ok) {
            this.credentials = null;
            throw new UploadError(response.status, `sync token request failed (${response.status})`);
        }
        const body = (await response.json());
        this.credentials = {
            endpoint: this.options.powersyncUrl ?? body.powersync_url,
            token: body.token,
            expiresAt: new Date(body.expires_at),
        };
        return this.credentials;
    }
    async uploadData(database) {
        if (this.outdated)
            return;
        const transaction = await database.getNextCrudTransaction();
        if (!transaction)
            return;
        const operations = transaction.crud.map(toUploadOperation);
        const request = { client_schema: CLIENT_SCHEMA_VERSION, operations };
        this.setStatus('uploading');
        let response;
        try {
            response = await this.fetchImpl(`${this.options.apiUrl}/v1/sync/upload`, {
                method: 'POST',
                headers: { ...this.headers(), 'content-type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(request),
            });
        }
        catch (error) {
            // Network failure: the SDK retries the same transaction with back-off.
            this.setStatus('error', error);
            throw error;
        }
        if (response.status === 426) {
            this.outdated = true;
            this.setStatus('outdated');
            return;
        }
        if (response.status === 401) {
            this.invalidateCredentials();
        }
        if (!response.ok) {
            const text = await response.text().catch(() => '');
            this.setStatus('error', { status: response.status, body: text });
            throw new UploadError(response.status, `upload failed (${response.status}): ${text}`);
        }
        const result = (await response.json());
        // Accepted: rejected operations are discarded locally and the server value prevails.
        await transaction.complete();
        this.options.onUploaded?.(result, operations);
        this.setStatus('idle');
    }
    headers() {
        return {
            [CLIENT_HEADERS.platform]: this.options.platform,
            [CLIENT_HEADERS.version]: this.options.appVersion,
            [CLIENT_HEADERS.schema]: String(CLIENT_SCHEMA_VERSION),
            ...(this.options.headers ?? {}),
        };
    }
    setStatus(status, detail) {
        this.status = status;
        this.options.onStatus?.(status, detail);
    }
}
//# sourceMappingURL=connector.js.map